import axios from 'axios';
import config from '../config';
import toast from '../toast';

const api = axios.create({ baseURL: config.apiUrl, withCredentials: true });

// Incluir JWT en requests si existe (fallback para iOS Safari que bloquea cookies cross-origin)
api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('auth_token');
  if (token) cfg.headers['Authorization'] = `Bearer ${token}`;
  return cfg;
});

api.interceptors.response.use(
  res => res,
  err => {
    const status = err.response?.status;

    if (status === 401) {
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
      return Promise.reject(err);
    }

    if (status === 403) {
      toast.error('No tenés permisos para realizar esta acción.');
      return Promise.reject(err);
    }

    if (status >= 500) {
      toast.error('Error en el servidor. Intentá de nuevo en unos segundos.');
      return Promise.reject(err);
    }

    if (!err.response) {
      toast.error('Sin conexión. Verificá tu red e intentá de nuevo.');
      return Promise.reject(err);
    }

    return Promise.reject(err);
  }
);

// Auth
export const getMe       = () => api.get('/auth/me');
export const getLoginUrl = () => api.get('/auth/login');
export const logout      = async () => {
  localStorage.removeItem('auth_token');
  return api.get('/auth/logout');
};

// Cotizaciones
export const getSolicitudes  = (q)           => api.get('/cotizaciones/', { params: q ? { q } : {} });
export const getSolicitud    = (id)          => api.get(`/cotizaciones/${id}`);
export const createSolicitud = (formData)    => api.post('/cotizaciones/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const cargarPrecios   = (id, items)   => api.post(`/precios/${id}`, { items });
export const marcarEnviada   = (id)          => api.post(`/cotizaciones/${id}/marcar-enviada`);

// Clientes
export const searchClientes  = (q)           => api.get('/clientes/', { params: { q } });
export const getClientes     = (params = {}) => api.get('/clientes/', { params });
export const syncClientes       = ()             => api.post('/clientes/sync');

// Admin
export const getStats           = ()         => api.get('/admin/stats');
export const getUsers           = ()         => api.get('/admin/users');
export const updateUser         = (id, data) => api.patch(`/admin/users/${id}`, data);
export const getAdminUsuarios   = ()         => api.get('/admin/usuarios');
export const patchAdminUsuario  = (id, data) => api.patch(`/admin/usuarios/${id}`, data);
export const createAdminUser    = (data)     => api.post('/admin/users', data);
export const getVendedores      = ()         => api.get('/admin/vendedores');
export const createVendedor     = (data)     => api.post('/admin/vendedores', data);
export const updateVendedor     = (id, data) => api.patch(`/admin/vendedores/${id}`, data);
export const getModulos         = ()         => api.get('/admin/modulos');
export const updateModulo       = (id, data) => api.patch(`/admin/modulos/${id}`, data);

// Guía de llamada
export const getGuiaConfig    = ()            => api.get('/guia/config');
export const iniciarLlamada   = (data = {})   => api.post('/guia/llamadas', data);
export const finalizarLlamada = (id, data)    => api.post(`/guia/llamadas/${id}/finalizar`, data);
export const getLlamadas      = (params)      => api.get('/guia/llamadas', { params });
export const getGuiaStats     = (params)      => api.get('/guia/stats', { params });
export const getDocs         = (categoria)   => api.get('/documentos/docs', { params: { categoria } });
export const uploadDoc       = (formData)    => api.post('/documentos/docs', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteDoc       = (id)          => api.delete(`/documentos/docs/${id}`);
export const getMedia        = (tipo)        => api.get('/documentos/media', { params: { tipo } });
export const uploadMedia     = (formData)    => api.post('/documentos/media', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteMedia     = (id)          => api.delete(`/documentos/media/${id}`);

// Dólar (proxy backend con cache)
export const getDolar = () => api.get('/dolar');

// Tango
export const getTangoDashboard = (params = {}) => api.get('/tango/dashboard', { params });
export const refreshTango      = ()   => api.post('/tango/refresh');
export const getTangoComercial = (params) => api.get('/tango/comercial', { params });

// System status
export const getSystemStatus   = ()   => api.get('/admin/system-status');

// ── Mi Cartera ────────────────────────────────────────────────────────────────
export const getCarteraVendedores          = ()             => api.get('/cartera/vendedores');
export const getCarteraPronostico          = (params = {}) => api.get('/cartera/pronostico', { params });
export const getCarteraPronosticoPendientes = (params = {}) => api.get('/cartera/pronostico/pendientes', { params });
export const getCarteraDashboard           = (params = {}) => api.get('/cartera/dashboard', { params });
export const syncCarteraPronostico         = ()             => api.post('/cartera/pronostico/sync');
export const gestionarPronostico           = (id, data)    => api.post(`/cartera/pronostico/${id}/gestionar`, data);

export const getCarteraSeguimiento   = (params = {}) => api.get('/cartera/seguimiento', { params });
export const gestionarSeguimiento    = (id, data)    => api.post(`/cartera/seguimiento/${id}/gestionar`, data);
export const syncCarteraSeguimiento  = ()             => api.post('/cartera/seguimiento/sync');
export const resetCarteraPronostico  = ()             => api.post('/cartera/pronostico/reset');
export const resetCarteraSeguimiento = ()             => api.post('/cartera/seguimiento/reset');

// ── Desarrollo de Cartera ─────────────────────────────────────────────────────
export const getDesarrolloResumen   = (params = {}) => api.get('/cartera/desarrollo/resumen',    { params });
export const getDesarrolloClientes  = (params = {}) => api.get('/cartera/desarrollo/clientes',   { params });
export const getDesarrolloCrossSell = (params = {}) => api.get('/cartera/desarrollo/cross-sell', { params });
export const invalidarCacheDesarrollo = ()           => api.post('/cartera/desarrollo/cache/invalidar');

// ── Desarrollos ──────────────────────────────────────────────────────────────
export const getDesarrollos          = (params = {}) => api.get('/desarrollos/', { params });
export const createDesarrollo        = (data)        => api.post('/desarrollos/', data);
export const getDesarrolloDetalle    = (id)          => api.get(`/desarrollos/${id}`);
export const gestionarDesarrollo     = (id, data)    => api.post(`/desarrollos/${id}/gestionar`, data);
export const comentarDesarrollo      = (id, texto)   => api.post(`/desarrollos/${id}/comentar`, { texto });
export const getDashboardDesarrollos = ()            => api.get('/desarrollos/dashboard');
// Trabajo espontáneo: solo lo puede crear el dev (el backend lo valida con el rol real).
export const createDesarrolloEspontaneo = (data)     => api.post('/desarrollos/espontaneo', data);
export const setDesarrolloWatchers   = (id, ids)     => api.put(`/desarrollos/${id}/watchers`, { watcher_ids: ids });
// Pasos: se manda la lista completa y el backend reconcilia por id.
export const setDesarrolloPasos      = (id, pasos)   => api.put(`/desarrollos/${id}/pasos`, { pasos });
export const togglePasoDesarrollo    = (id, pasoId)  => api.post(`/desarrollos/${id}/pasos/${pasoId}/toggle`);
// Capacidad: informativo. El backend nunca rechaza una fecha por sobreasignación.
export const getCapacidadDesarrollos = (params = {}) => api.get('/desarrollos/capacidad', { params });
export const getCargaDesarrollos     = (params = {}) => api.get('/desarrollos/carga', { params });
export const getConfigDesarrollos    = ()            => api.get('/desarrollos/config');
export const setJornadaDesarrollos   = (dias)        => api.put('/desarrollos/jornada', { dias });
export const getAgendaDesarrollos    = (params = {}) => api.get('/desarrollos/agenda', { params });
export const createBloqueoAgenda     = (data)        => api.post('/desarrollos/agenda', data);
export const deleteBloqueoAgenda     = (id)         => api.delete(`/desarrollos/agenda/${id}`);

// ── Devoluciones ─────────────────────────────────────────────────────────────
export const getDevolucionesPedidosTalonarios = ()               => api.get('/devoluciones/pedidos/talonarios');
export const getDevolucionesPedidos           = (params = {})    => api.get('/devoluciones/pedidos', { params });
export const syncDevolucionesPedidos          = ()               => api.post('/devoluciones/pedidos/sync');
export const getDevoluciones                  = (params = {})    => api.get('/devoluciones/', { params });
export const createDevolucion                 = (data)           => api.post('/devoluciones/', data);
export const getDevolucion                    = (id)             => api.get(`/devoluciones/${id}`);
export const recibirDevolucion                = (id)             => api.post(`/devoluciones/${id}/recibir`);
export const completarControlDevolucion       = (id, data)       => api.post(`/devoluciones/${id}/control`, data);
export const validarComercialDevolucion       = (id, data)       => api.post(`/devoluciones/${id}/validar`, data);
export const emitirNotaDevolucion             = (id, data)       => api.post(`/devoluciones/${id}/emitir-nota`, data);
export const confirmarTransferenciaDevolucion = (id, data)       => api.post(`/devoluciones/${id}/transferencia`, data);
export const cancelarDevolucion               = (id)             => api.post(`/devoluciones/${id}/cancelar`);
export const deleteDevolucion                 = (id)             => api.delete(`/devoluciones/${id}`);

// ── Facturación ───────────────────────────────────────────────────────────────
export const getFacturacionDashboard = (params = {}) => api.get('/facturacion/dashboard', { params });
export const syncFacturacion         = ()             => api.post('/facturacion/sync');

// Proyección comercial
export const getProyeccion            = (params = {}) => api.get('/proyeccion', { params });
export const actualizarProyeccion     = (params = {}) => api.post('/proyeccion/actualizar', null, { params });
export const getProyeccionAnual       = (anio)        => api.get('/proyeccion/anual', { params: { anio } });
export const getProyeccionPendientes  = (params = {}) => api.get('/proyeccion/pendientes', { params });
export const getObjetivosProyeccion   = (anio)        => api.get('/proyeccion/objetivos', { params: { anio } });
export const saveObjetivosProyeccion  = (anio, items) => api.put('/proyeccion/objetivos', { anio, items });
export const getNoLaborables          = (anio)        => api.get('/proyeccion/no-laborables', { params: { anio } });
export const addNoLaborable           = (data)        => api.post('/proyeccion/no-laborables', data);
export const deleteNoLaborable        = (id)          => api.delete(`/proyeccion/no-laborables/${id}`);

// ── Inventario ────────────────────────────────────────────────────────────────
export const importarSaldos      = (fd)            => api.post('/inventario/importar/saldos', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
export const importarPosiciones  = (fd)            => api.post('/inventario/importar/posiciones', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
export const importarPrecios     = (fd)            => api.post('/inventario/importar/precios', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
export const importarPartidas    = (fd)            => api.post('/inventario/importar/partidas', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getImportaciones    = ()              => api.get('/inventario/importaciones');
export const getFrescuraInv      = ()              => api.get('/inventario/frescura');
export const getImportacion      = (id)            => api.get(`/inventario/importaciones/${id}`);

export const getInventarioArticulos       = (params = {}) => api.get('/inventario/articulos', { params });
export const getArticulosUbicaciones      = (params = {}) => api.get('/inventario/articulos-ubicaciones', { params });
export const getInventarioUbicaciones     = (params = {}) => api.get('/inventario/ubicaciones', { params });
export const getUbicacionesNiveles        = (params = {}) => api.get('/inventario/ubicaciones/niveles', { params });
export const getInventarioDepositos       = ()             => api.get('/inventario/depositos');
export const getFamilias                  = ()             => api.get('/inventario/familias');
export const getClasificaciones           = ()             => api.get('/inventario/clasificaciones');
export const getCalidadMaestro            = ()             => api.get('/inventario/reportes/calidad');

export const getSesionesInv      = (params = {})   => api.get('/inventario/sesiones', { params });
export const getSesionInv        = (id)            => api.get(`/inventario/sesiones/${id}`);
export const createSesionInv     = (data)          => api.post('/inventario/sesiones', data);
export const deleteSesionInv     = (id)            => api.delete(`/inventario/sesiones/${id}`);
export const abrirSesionInv      = (id, data = {}) => api.patch(`/inventario/sesiones/${id}/abrir`, data);
export const cerrarSesionInv     = (id)            => api.patch(`/inventario/sesiones/${id}/cerrar`);
export const cancelarSesionInv   = (id)            => api.patch(`/inventario/sesiones/${id}/cancelar`);
export const conciliarSesionInv  = (id)            => api.patch(`/inventario/sesiones/${id}/conciliar`);
export const addAlcanceInv       = (id, data)      => api.post(`/inventario/sesiones/${id}/alcance`, data);
export const deleteAlcanceInv    = (sesionId, alcanceId) => api.delete(`/inventario/sesiones/${sesionId}/alcance/${alcanceId}`);
export const createGrupoInv      = (id, data)      => api.post(`/inventario/sesiones/${id}/grupos`, data);
export const addUsuarioGrupoInv  = (gid, data)     => api.post(`/inventario/grupos/${gid}/usuarios`, data);
export const getUsuariosInv      = ()              => api.get('/inventario/usuarios');

export const registrarConteoInv  = (sid, data)     => api.post(`/inventario/sesiones/${sid}/conteos`, data);
export const getConteosInv       = (sid, params = {}) => api.get(`/inventario/sesiones/${sid}/conteos`, { params });
export const corregirConteoInv   = (id, data)      => api.patch(`/inventario/conteos/${id}`, data);

export const getDiferenciasInv        = (sid, params = {}) => api.get(`/inventario/sesiones/${sid}/diferencias`, { params });
export const updateDiferenciaInv      = (did, data)        => api.patch(`/inventario/diferencias/${did}`, data);
export const registrarAjusteInv       = (did, data)        => api.post(`/inventario/diferencias/${did}/ajuste`, data);
export const bulkUpdateDiferenciasInv = (sid, data)        => api.patch(`/inventario/sesiones/${sid}/diferencias/bulk`, data);
export const exportarCSVInv           = (sid, formato = 'tango') => api.get(`/inventario/sesiones/${sid}/exportar-csv`, { params: { formato }, responseType: 'blob' });
export const exportarAjusteTangoInv   = (sid, params = {}) => api.get(`/inventario/sesiones/${sid}/exportar-ajuste-tango`, { params, responseType: 'blob' });
export const getResumenAjusteTangoInv = (sid, params = {}) => api.get(`/inventario/sesiones/${sid}/ajuste-tango/resumen`, { params });
export const getEstadoAjusteTangoInv  = (sid)              => api.get(`/inventario/sesiones/${sid}/ajuste-tango/estado`);
export const marcarAjusteCargadoInv   = (sid, tipo)        => api.post(`/inventario/sesiones/${sid}/ajuste-tango/cargado`, { tipo });
export const desmarcarAjusteCargadoInv = (sid, tipo)       => api.delete(`/inventario/sesiones/${sid}/ajuste-tango/cargado/${tipo}`);
export const marcarInventariableInv   = (data)             => api.patch('/inventario/articulos/inventariable', data);
export const getBalanceSesionesInv   = ()                 => api.get('/inventario/balances/sesiones');
export const calcularBalanceInv      = (sesion_ids)       => api.post('/inventario/balances/calcular', { sesion_ids });
export const getBalancesInv          = ()                 => api.get('/inventario/balances');
export const crearBalanceInv         = (data)             => api.post('/inventario/balances', data);
export const getBalanceInv           = (id)               => api.get(`/inventario/balances/${id}`);
export const eliminarBalanceInv      = (id)               => api.delete(`/inventario/balances/${id}`);
export const exportarBalanceInv      = (id)               => api.get(`/inventario/balances/${id}/exportar`, { responseType: 'blob' });

export const getReporteSinUbicacion           = () => api.get('/inventario/reportes/sin-ubicacion');
export const getReporteMultiplesUbicaciones   = () => api.get('/inventario/reportes/multiples-ubicaciones');
export const getReporteUbicacionesSinArticulos = () => api.get('/inventario/reportes/ubicaciones-sin-articulos');
export const getReporteDiferencias            = (sid) => api.get(`/inventario/sesiones/${sid}/reporte-diferencias`);

// ── Calculadora de cuotas ─────────────────────────────────────────────────────
export const getModalidadesCuotas  = ()            => api.get('/cuotas/modalidades');
export const saveModalidadesCuotas = (modalidades) => api.put('/cuotas/modalidades', { modalidades });

// ── Firmas de Gmail ───────────────────────────────────────────────────────────
const enc = (email) => encodeURIComponent(email);
export const getFirmasEstado      = ()              => api.get('/firmas/estado');
export const getFirmasUsuarios    = (refrescar)     => api.get('/firmas/usuarios', { params: refrescar ? { refrescar: 1 } : {} });
export const getFirmaUsuario      = (email)         => api.get(`/firmas/usuarios/${enc(email)}/firma`);
export const aplicarFirmaUsuario  = (email, html)   => api.post(`/firmas/usuarios/${enc(email)}/firma`, { html });
export const sincronizarFirmas    = ()              => api.post('/firmas/sincronizar');
export const saveFirmaPerfil      = (email, data)   => api.put(`/firmas/perfiles/${enc(email)}`, data);
export const getFirmaVariables    = ()              => api.get('/firmas/variables');
export const saveFirmaVariables   = (variables)     => api.put('/firmas/variables', { variables });
export const getFirmaPlantillas   = ()              => api.get('/firmas/plantillas');
export const createFirmaPlantilla = (data)          => api.post('/firmas/plantillas', data);
export const updateFirmaPlantilla = (id, data)      => api.put(`/firmas/plantillas/${id}`, data);
export const deleteFirmaPlantilla = (id)            => api.delete(`/firmas/plantillas/${id}`);
export const getFirmaComponentes   = ()             => api.get('/firmas/componentes');
export const createFirmaComponente = (data)         => api.post('/firmas/componentes', data);
export const updateFirmaComponente = (id, data)     => api.put(`/firmas/componentes/${id}`, data);
export const deleteFirmaComponente = (id)           => api.delete(`/firmas/componentes/${id}`);
export const getFirmaImagenes     = ()              => api.get('/firmas/imagenes');
export const uploadFirmaImagen    = (formData)      => api.post('/firmas/imagenes', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const updateFirmaImagen    = (id, data)      => api.patch(`/firmas/imagenes/${id}`, data);
export const recortarFirmaImagen  = (id, data)      => api.post(`/firmas/imagenes/${id}/recortar`, data);
export const deleteFirmaImagen    = (id)            => api.delete(`/firmas/imagenes/${id}`);
export const deleteFirmaImagenes  = (ids)           => api.post('/firmas/imagenes/eliminar', { ids });

// ── Arcade ──────────────────────────────────────────────────────────────────
// Devuelve el catálogo y la `base_url` desde donde se sirven los juegos: viven
// en el backend (detrás del login), no en el dominio del portal.
export const getArcadeJuegos = () => api.get('/arcade/juegos');
export const getArcadeResumen = () => api.get('/arcade/resumen');
export const getArcadeRanking = (juegoId, categoria) =>
  api.get(`/arcade/ranking/${juegoId}`, { params: categoria ? { categoria } : {} });

// ── Automatizaciones (monitor) ────────────────────────────────────────────────
// El semáforo lo publica monitor.py desde la PC de la oficina; acá solo se lee.
export const getAutomatizaciones = () => api.get('/automatizaciones');
export const getAutomatizacionHistorial = (id, dias = 30) =>
  api.get(`/automatizaciones/${encodeURIComponent(id)}/historial`, { params: { dias } });

export default api;
